// Types and pure helpers for the staff usage dashboard. The server shapes are
// in server/src/services/usageAnalytics.ts and routes/agencyUsageRoutes.ts.

export interface UsageCategory {
  key: string;
  label: string;
}

export interface UsageMetricMeta {
  key: string;
  label: string;
  category: string;
  trackedSince: boolean;
}

export interface MetricTotals {
  current: number;
  previous: number;
  users: number;
}

export interface UsageSummary {
  totals: Record<string, MetricTotals>;
  activeUsers: { current: number; previous: number };
  series: Array<Record<string, number | string>>;
}

export interface TimeSaved {
  aiCases: number;
  defaultHours: number;
  personalHours: number;
  effectiveWpm: number;
  medianTypingWpm: number | null;
  typingSamples: number;
  personalized: boolean;
}

export interface AgencyTimeSaved {
  aiCases: number;
  defaultHours: number;
  personalHours: number;
  officers: number;
  personalizedOfficers: number;
}

export interface EntityRow {
  id: string;
  name: string;
  sub: string | null;
  officers?: number;
  lastActive: string | null;
  metrics: Record<string, number>;
  timeSaved?: TimeSaved | null;
}

export interface FeedItem {
  at: string;
  type: string;
  label: string;
  detail: string | null;
  caseId: string | null;
  investigationId: string | null;
}

export interface RangeInfo {
  from: string;
  to: string;
  prevFrom: string;
  granularity: "day" | "week" | "month";
}

export interface UsageResponse {
  level: "overview" | "agency" | "officer";
  range: RangeInfo;
  summary: UsageSummary;
  rows?: EntityRow[];
  agency?: { id: string; name: string; state?: string | null };
  officer?: { id: string; name: string; email: string; createdAt: string };
  feed?: FeedItem[];
  timeSaved?: TimeSaved | AgencyTimeSaved | null;
  typing?: { medianWpm: number | null; p25Wpm: number | null; p75Wpm: number | null; samples: number; effectiveWpm: number; personalized: boolean } | null;
}

export interface CatalogResponse {
  categories: UsageCategory[];
  metrics: UsageMetricMeta[];
  typing: { defaultWpm: number; compositionFactor: number; minSamples: number };
  // First day a counter was recorded; null until tracking has run once.
  trackingStartedAt: string | null;
}

// ---- Range presets ----------------------------------------------------------

export type RangePreset = "7d" | "30d" | "90d" | "ytd" | "custom";

export const RANGE_OPTIONS: Array<{ value: RangePreset; label: string }> = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "ytd", label: "Year to date" },
  { value: "custom", label: "Custom range" },
];

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** Inclusive from/to (UTC dates) for a preset. `custom` passes its own through. */
export function resolvePreset(preset: RangePreset, custom?: { from?: string; to?: string }, now: Date = new Date()) {
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const back = (days: number) => new Date(today.getTime() - (days - 1) * 86_400_000);
  switch (preset) {
    case "7d":
      return { from: iso(back(7)), to: iso(today) };
    case "90d":
      return { from: iso(back(90)), to: iso(today) };
    case "ytd":
      return { from: `${today.getUTCFullYear()}-01-01`, to: iso(today) };
    case "custom":
      return { from: custom?.from || iso(back(30)), to: custom?.to || iso(today) };
    default:
      return { from: iso(back(30)), to: iso(today) };
  }
}

// ---- Number helpers ---------------------------------------------------------

/** Percent change vs the previous period; null when there is nothing to compare. */
export function pctDelta(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

export function formatCount(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}

export function formatHours(h: number): string {
  return h >= 100 ? `${Math.round(h).toLocaleString("en-US")} h` : `${h.toFixed(1)} h`;
}

export function formatWhen(iso: string | null): string {
  if (!iso) return "Never";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Sum of a row's metrics, optionally restricted to a set of metric keys. */
export function sumMetrics(metrics: Record<string, number>, keys?: string[]): number {
  if (keys) return keys.reduce((a, k) => a + (metrics[k] || 0), 0);
  return Object.values(metrics).reduce((a, b) => a + b, 0);
}

// ---- Chart buckets ------------------------------------------------------------

/** Start-of-bucket dates (YYYY-MM-DD) covering [from, to], matching the server's date_trunc. */
export function buildBuckets(from: string, to: string, granularity: "day" | "week" | "month"): string[] {
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  const out: string[] = [];
  const cursor = new Date(start);
  if (granularity === "week") {
    // Postgres weeks start on Monday.
    const dow = (cursor.getUTCDay() + 6) % 7;
    cursor.setUTCDate(cursor.getUTCDate() - dow);
  } else if (granularity === "month") {
    cursor.setUTCDate(1);
  }
  while (cursor <= end) {
    out.push(iso(cursor));
    if (granularity === "day") cursor.setUTCDate(cursor.getUTCDate() + 1);
    else if (granularity === "week") cursor.setUTCDate(cursor.getUTCDate() + 7);
    else cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return out;
}
